import type { PerronPointsEntryType } from '../../shared/models/perron-points.model';

/**
 * Copy de cara al cliente del perfil (B10, spec `perfil-usuario-b10-plan.md` §7.2), en UN único archivo
 * para permitir tests verbatim: si alguien edita un texto, `profile.copy.spec.ts` lo delata. Tuteo, sin
 * voseo: la guardia del spec recorre todas las cadenas de este objeto, también las que añadan W0b-W3.
 *
 * W0a trae pestañas, menú de usuario y contenedor sin sesión; W0b, la cabecera del perfil y el corazón de la cabecera
 * global; W1, «Mis pedidos»; W2, la lista de deseos; W3, los ajustes. La cabecera global importa este objeto, así que
 * vive en el bundle inicial: solo texto.
 */
export const PROFILE_COPY = {
  /** Pestañas del contenedor, en su orden. «Lista de deseos», nunca «Wishlist» (B30). */
  TABS: {
    ORDERS: 'Pedidos',
    /** Perrón Coins (A0·web): segunda pestaña. */
    COINS: 'Perrón Coins',
    WISHLIST: 'Lista de deseos',
    SETTINGS: 'Ajustes',
    COMMUNITY: 'Comunidad',
  },
  /** Nombre accesible de la barra de pestañas. */
  TABS_NAV_LABEL: 'Secciones del perfil',

  /** Enlaces del menú de usuario de la cabecera global (W0b). */
  USER_MENU: {
    PROFILE: 'Mi perfil',
    ORDERS: 'Mis pedidos',
    WISHLIST: 'Lista de deseos',
  },
  /** Nombre accesible del corazón de la cabecera global, que lleva a `/perfil/deseos` (W0b). */
  HEADER_WISHLIST_ARIA: 'Lista de deseos',

  /** Cabecera del perfil (W0b). */
  HEADER: {
    /** Plantilla: `{mes}` en minúscula y `{año}`, en hora de Costa Rica (`formatClientSince`). */
    CLIENT_SINCE: 'Cliente desde {mes} de {año}',
    /** Aviso B24: solo cuentas `cliente` con `verified === false`. */
    UNVERIFIED_EMAIL: 'Tu correo todavía no está verificado.',
  },

  /** Migas del contenedor. */
  BREADCRUMB_HOME: 'Inicio',
  BREADCRUMB_PROFILE: 'Mi perfil',

  /** `h1` del contenedor sin sesión (con sesión lo pone la cabecera del perfil, W0b). */
  PAGE_TITLE: 'Mi perfil',

  /** Contenedor sin sesión (B27): sin guard, texto + botón al modal de login. */
  SIGNED_OUT: {
    MESSAGE: 'Inicia sesión para ver tus pedidos, tu lista de deseos y tus ajustes.',
    LOGIN_BUTTON: 'Iniciar sesión',
  },

  /** Listado de «Mis pedidos» (W1). */
  ORDERS: {
    /** §7.2. */
    LOAD_ERROR: 'No pudimos cargar tus pedidos. Inténtalo de nuevo.',
    RETRY: 'Reintentar',
    /** Estado vacío actual (§5.6.3), con la mascota. */
    EMPTY_TITLE: 'Aún no tienes pedidos',
    EMPTY_TEXT: 'Explora nuestra tienda y encuentra tus cartas favoritas.',
    EMPTY_CTA: 'Explorar tienda',
    /**
     * Cuenta `cliente` sin verificar (B44), APROBADO por Adrián el 30/09/2026 (sustituye a la fila de §7.2, falsa con
     * B44). `{ajustes}` es el enlace a `/perfil/ajustes` con el texto de `UNVERIFIED_SETTINGS_LINK`.
     */
    UNVERIFIED: 'Verifica tu correo para ver tus pedidos. Si no te llegó el correo, puedes pedirlo de nuevo en {ajustes}.',
    UNVERIFIED_SETTINGS_LINK: 'Ajustes',
    /** `{n}` artículos del pedido (`items_count`). */
    ITEMS_ONE: '{n} artículo',
    ITEMS_OTHER: '{n} artículos',
    VIEW_DETAIL: 'Ver detalle',
    /** Bloque de precompras vivas encima del listado (W1b; B35, §5.3 ter), título aprobado. */
    PREPURCHASES_TITLE: 'Precompras en curso',
  },

  /** Detalle `/perfil/pedidos/:id` (W1). */
  ORDER_DETAIL: {
    BACK: 'Volver a mis pedidos',
    /** Espejo de `ProfileCopy::ORDER_NOT_FOUND` de la API: el texto del 404 y el de un `:id` no numérico. */
    NOT_FOUND: 'No encontramos ese pedido.',
    PRODUCTS: 'Productos',
    QUANTITY: 'Cantidad: {n}',
    DELIVERY: 'Entrega',
    DISCOUNT: 'Descuento',
    TOTAL: 'Total',
    PAYMENT: 'Pago',
    PAYMENT_METHOD: 'Método de pago',
    RECEIPTS: 'Abonos',
    /** Dato que falta en un abono (método en los históricos, fecha). */
    MISSING: '—',
    /** §7.2. */
    PROOF_RECEIVED: 'Comprobante recibido',
    /** Apartado vivo, §7.2. `{pending}` sin símbolo (`formatInteger`): el `₡` ya está en la plantilla. */
    LAYAWAY_PENDING: 'Saldo pendiente: ₡{pending} · Fecha límite: {dd/mm/aaaa}',
    LAYAWAY_VERIFYING: 'Estamos verificando tu abono. Fecha límite: {dd/mm/aaaa}',
    /** Apartado vivo sin abono registrado ni comprobante (hueco de §7.2), APROBADO por Adrián el 30/09/2026. */
    LAYAWAY_NO_PAYMENT: 'Aún no registramos tu abono. Fecha límite: {dd/mm/aaaa}',
  },

  /**
   * Ajustes `/perfil/ajustes` y botón de reenvío de la cabecera (W3, §5.6.5). Los espejos de `ProfileCopy` de la API
   * (§7.1) van marcados; los títulos, etiquetas, botones, `EMAIL_HINT` y `SAVE_ERROR` (huecos de §7.2), APROBADOS por
   * Adrián el 02/10/2026. Los 422 de «Reenviar» y «Enviar enlace» pintan el texto del servidor: no están aquí.
   */
  SETTINGS: {
    /** Espejo de `ProfileCopy::PROFILE_CLIENT_ONLY`: cuentas que no son `cliente`, sin formularios. */
    CLIENT_ONLY: 'Tu cuenta es del equipo de Perrón Store: tus datos los cambia un administrador.',
    PERSONAL_TITLE: 'Datos personales',
    NAME_LABEL: 'Nombre',
    SURNAMES_LABEL: 'Apellidos',
    PHONE_LABEL: 'Teléfono (opcional)',
    SAVE_BUTTON: 'Guardar cambios',
    /** Espejo de `ProfileCopy::PROFILE_UPDATED`, por si el 200 no trajera `message`. */
    SAVED: 'Guardamos tus datos.',
    SAVE_ERROR: 'No pudimos guardar tus datos. Inténtalo de nuevo.',
    /** Espejos de `ProfileCopy::NAME_REQUIRED` / `SURNAMES_REQUIRED`: la validación previa de vacío. */
    NAME_REQUIRED: 'Escribe tu nombre.',
    SURNAMES_REQUIRED: 'Escribe tus apellidos.',

    EMAIL_TITLE: 'Correo',
    EMAIL_LABEL: 'Correo',
    EMAIL_HINT: 'Si lo cambias, tendrás que verificarlo de nuevo.',
    EMAIL_BUTTON: 'Cambiar correo',
    EMAIL_INVALID: 'Escribe un correo válido.',
    /** Espejo de `ProfileCopy::EMAIL_TAKEN` (A3-bis): el 422 de correo en uso se reconoce comparando con este texto. */
    EMAIL_TAKEN: 'Ese correo ya lo usa otra cuenta.',
    EMAIL_ERROR: 'No pudimos cambiar tu correo. Revisa que no lo uses en otra cuenta o inténtalo de nuevo.',
    /** Plantilla: `{nuevo}` es el correo que devuelve la API. */
    EMAIL_CHANGED:
      'Te enviamos un correo a {nuevo} para verificarlo. Mientras no lo verifiques, no verás tus pedidos ni el directorio de jugadores.',

    PASSWORD_TITLE: 'Contraseña',
    /** Plantilla: `{correo}` es el correo de la sesión. */
    PASSWORD_TEXT: 'Te enviaremos a {correo} un enlace para cambiar tu contraseña.',
    PASSWORD_BUTTON: 'Enviar enlace',
    PASSWORD_SENT: 'Listo: revisa tu correo.',

    RESEND_BUTTON: 'Reenviar correo de verificación',
    RESEND_SENT: 'Te enviamos el correo de verificación.',
    /** Botones de correo en espera (B43). Plantillas: `{s}` son los segundos que quedan. */
    RESEND_WAIT: 'Reenviar en {s} s',
    LINK_WAIT: 'Enviar enlace en {s} s',
    /** 429 del limitador `account-mail` (B43), el mismo texto que `ProfileCopy::ACCOUNT_MAIL_RATE_LIMITED`. */
    MAIL_RATE_LIMITED: 'Ya te lo enviamos hace un momento. Espera un poco antes de pedirlo otra vez.',
    /** Fallo de «Reenviar» y de «Enviar enlace» (5xx, red o un 422 sin texto). */
    MAIL_ERROR: 'No pudimos enviar el correo. Inténtalo de nuevo.',
  },

  /**
   * «Mis Coins» (`/perfil/coins`, A0·web de Perrón Coins). El valor del Coin nunca va escrito: `{valor}` sale de
   * `coin_value_crc` de `/perron-points/me` con `formatCrc`, y en futuro («valdrá»): aún no se gastan como dinero (B3).
   * Las cantidades de Coins (`{n}`) van con `formatInteger`: no son dinero.
   */
  COINS: {
    TITLE: 'Mis Coins',
    BALANCE_LABEL: 'Tu saldo',
    BALANCE_ONE: '{n} Coin',
    BALANCE_OTHER: '{n} Coins',
    LIFETIME_LABEL: 'Ganados en total',
    LIFETIME_HINT: 'Todo lo que has ganado desde el principio. Gastar Coins no lo reduce.',
    VIP_ACTIVE: 'Tienes acceso VIP a las precompras exclusivas.',
    /** `{n}` = `max(0, threshold − balance)`. */
    VIP_PENDING: 'Te faltan {n} Coins para el acceso VIP.',
    /** Singular de `VIP_PENDING` (`n = 1`), A1·web. */
    VIP_PENDING_ONE: 'Te falta 1 Coin para el acceso VIP.',
    VALUE: 'Pronto podrás pagar con tus Coins: cada uno valdrá {valor}.',
    REDEEM_CTA: 'Canjear un código',
    HOW_LINK: '¿Cómo funcionan los Coins?',
    HISTORY_TITLE: 'Movimientos',
    /** `{n}` = `preorder_id`; enlaza a `/perfil/pedidos/{n}`. */
    ORDER_REF: 'Pedido #{n}',
    LOAD_MORE: 'Ver más movimientos',
    /** Toast si falla «Ver más» (A1·web): lo pintado se queda y el botón sigue para reintentar. */
    LOAD_MORE_ERROR: 'No pudimos cargar más movimientos. Inténtalo de nuevo.',
    LOAD_ERROR: 'No pudimos cargar tus Coins. Inténtalo de nuevo.',
    RETRY: 'Reintentar',
    EMPTY_TITLE: 'Aún no tienes Coins',
    EMPTY_TEXT: 'Gana Coins con tus compras o canjeando un código.',
    /** Un tipo que la web aún no conoce. */
    TYPE_FALLBACK: 'Movimiento',
    /** Etiqueta por tipo del ledger: el `satisfies` obliga a cubrir los 13 de `PERRON_POINTS_ENTRY_TYPES`. */
    TYPES: {
      purchase_accrual: 'Compra',
      purchase_reversal: 'Compra anulada',
      code_redemption: 'Código canjeado',
      code_reversal: 'Código anulado',
      raffle_entry: 'Participación en rifa',
      raffle_refund: 'Devolución de rifa',
      opportunity_purchase: 'Oportunidad canjeada',
      coin_payment: 'Pago con Coins',
      coin_payment_refund: 'Devolución de pago con Coins',
      campaign_bonus: 'Bonificación de campaña',
      collaboration_bonus: 'Colaboración en el blog',
      referral_bonus: 'Amigo referido',
      birthday_bonus: 'Regalo de cumpleaños',
    } satisfies Record<PerronPointsEntryType, string>,
    /** «Mi QR Perrón» (A1·web, D16-D21 de `perron-coins-plan.md`): tarjeta arriba de `/perfil/coins`. */
    QR: {
      TITLE: 'Mi QR Perrón',
      TEXT: 'Enséñalo en caja al pagar y sumamos tus Coins al momento.',
      ALT: 'Tu QR Perrón',
      BRIGHTNESS_HINT: 'Si el lector no lo lee a la primera, sube el brillo de la pantalla.',
      LOAD_ERROR: 'No pudimos cargar tu QR. Inténtalo de nuevo.',
      RETRY: 'Reintentar',
      /** Espejo de `PerronCoinsCopy::QR_CLIENTS_ONLY` de la API: el 403 de `/perron-coins/qr/me` y de `…/regenerate`. */
      CLIENTS_ONLY: 'Tu QR Perrón solo está disponible en cuentas de cliente.',
      REGENERATE: 'Generar un QR nuevo',
      REGENERATE_CONFIRM: 'Tu QR actual dejará de funcionar. ¿Generamos uno nuevo?',
      REGENERATE_YES: 'Sí, generar uno nuevo',
      REGENERATE_CANCEL: 'Cancelar',
      REGENERATE_DONE: 'Listo: este es tu QR nuevo. El anterior ya no funciona.',
      /**
       * Espejo de `PerronCoinsCopy::QR_REGENERATE_RATE_LIMITED` de la API: el 429 del throttle. Se pinta el texto del
       * servidor si llega; este es el respaldo.
       */
      REGENERATE_RATE_LIMITED: 'Ya generaste un QR nuevo hace un momento. Espera unos minutos antes de volver a hacerlo.',
      REGENERATE_ERROR: 'No pudimos generar un QR nuevo. Inténtalo de nuevo.',
    },
  },

  /**
   * Lista de deseos (W2, §7.2): corazón (aria y toasts), carga y vacío de `/perfil/deseos`. Los 422 del corazón
   * («no disponible», «llena») son texto del servidor: no están aquí.
   */
  WISHLIST: {
    ARIA_SAVE: 'Guardar en tu lista de deseos',
    ARIA_REMOVE: 'Quitar de tu lista de deseos',
    SAVED: 'Lo guardamos en tu lista de deseos.',
    REMOVED: 'Lo quitamos de tu lista de deseos.',
    TOGGLE_ERROR: 'No pudimos actualizar tu lista de deseos. Inténtalo de nuevo.',
    LOAD_ERROR: 'No pudimos cargar tu lista de deseos. Inténtalo de nuevo.',
    EMPTY_TITLE: 'Tu lista de deseos está vacía',
    EMPTY_TEXT: 'Toca el corazón de cualquier producto para guardarlo aquí.',
  },

  /**
   * Barra, orden y fecha de la pestaña `deseos`: textos de la pestaña vieja (`tab-wishlist`), movidos aquí SIN
   * cambiarlos (W2). `EMPTY_CTA` es el mismo «Explorar tienda» del vacío de «Mis pedidos».
   */
  WISHLIST_TAB: {
    /** `{n}` productos de la lista. */
    COUNT_ONE: '{n} producto',
    COUNT_OTHER: '{n} productos',
    SORT_PLACEHOLDER: 'Ordenar por',
    SORT: {
      DATE_DESC: 'Recientes primero',
      DATE_ASC: 'Más antiguos primero',
      PRICE_ASC: 'Precio: menor a mayor',
      PRICE_DESC: 'Precio: mayor a menor',
      NAME_ASC: 'Nombre: A → Z',
      NAME_DESC: 'Nombre: Z → A',
    },
    VIEW_LABEL: 'Vista',
    VIEW_GRID: 'Vista cuadrícula',
    VIEW_LIST: 'Vista lista',
    /** `{fecha}` en `dd/mm/aaaa`, hora de Costa Rica (`formatIsoDateTimeAsDate`). */
    ADDED_AT: 'Agregado el {fecha}',
    EMPTY_CTA: 'Explorar tienda',
  },
} as const;
